import { useCallback, useEffect, useState } from 'react';
import type {
  MountableApp,
  ModuleMountOptions,
  HostingEnvironment,
  GetTokenSilentlyOptions,
  GetTokenSilentlyVerboseResponse,
  IdToken,
  Auth0State,
} from '@opengov/psp-contract';
import type { RefObject } from 'react';
import type { User } from '@auth0/auth0-react';
import { createShellBridge, shellBridgeStore, shellEventBus } from '../services/shellBridge';
import { createModuleAnalytics } from '../services/analytics/ogAnalytics';
import type {
  Auth0Provider,
  EntityProvider,
  NavigationProvider,
} from '../services/shellBridge/createShellBridge';
import { startModuleHealthPoll } from '../services/moduleHealthPoll';
import { registerMountedModule } from '../services/mountedModulesRegistry';
import {
  endsSessionWhenUnmounted,
  rememberSessionEnd,
  sessionEndModuleUrl,
} from '../services/sessionEndApps';
import type { RemoteModuleConfig } from '../types';
import { useLoggerWithContext } from '@/services/logger/loggerContext';

/**
 * Mount args as the shell holds them *before* the per-mount `ShellBridge` is
 * created. `shell` is required on `ModuleMountOptions`, but the shell attaches
 * it only once `createShellBridge` runs at mount time. This hook owns that
 * attachment: callers supply everything except `shell`, and the merge below
 * produces the full `ModuleMountOptions` passed to `module.mount()`.
 */
export type PreBridgeMountArgs = Omit<ModuleMountOptions, 'shell'>;

/**
 * Latest auth values the host (RemoteAppShell or UnauthenticatedRemoteAppShell)
 * holds for the mounted module. Stored in a ref so updates don't trigger remounts.
 *
 * `user`, `getAccessTokenSilently`, `getIdTokenClaims`, `logout` come from the
 * Auth0 SDK via `useAuth()` and are passed through unchanged so the mounted
 * module sees Auth0's own semantics.
 */
export interface Auth0Ref {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | undefined;
  error: Error | undefined;
  getAccessTokenSilently: {
    (
      options: GetTokenSilentlyOptions & { detailedResponse: true }
    ): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
  };
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  /** PSP's wrapped logout. Runs the logout fan-out (modules' `onSessionEnd` + `unmount`), then
   *  requests the Auth0 redirect. Resolves once the redirect has been requested, and rejects if
   *  that request fails. Bridge.requestLogout awaits this, so a module's call rejects too. */
  logout: () => Promise<void>;
}

interface UseShellBridgeMountOptions {
  /** The loaded remote module (null while loading) */
  module: MountableApp | null;
  /** Ref to the container div where the module mounts */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Ref to the latest mount args, minus `shell` (this hook attaches the
   *  bridge). Held in a ref to avoid dependency-triggered remounts. */
  mountArgsRef: RefObject<PreBridgeMountArgs | null>;
  /** Ref to the latest auth values — the bridge reads through to this on every
   *  call so token refreshes and auth state changes flow without remounting. */
  auth0Ref: RefObject<Auth0Ref | null>;
  /** Additional values that trigger remount when changed; must all be truthy for mount to proceed */
  remountDeps: unknown[];
  /** App registry config — provides appId and basePath for bridge creation and logging */
  appConfig: RemoteModuleConfig;
  /** Current hosting environment — included in shell state snapshot */
  hostingEnvironment: HostingEnvironment;
  /** Ref to the showDebugInfo flag (avoids re-triggering mount on prop change) */
  showDebugInfoRef: RefObject<boolean | undefined>;
  /** Ref to the resolved module URL (debug logging, and checking a module sign-out can reload) */
  resolvedModuleUrlRef: RefObject<string | null>;
  /**
   * Ref to the registry route pattern the module URL was resolved from. Optional — only routed
   * mounts (`RemoteAppShell`) supply it. Without it, a module that opts in to
   * `sessionEndWhenUnmounted` can't be found again at sign-out, so its session is ended only
   * while it is mounted.
   */
  resolvedModuleRoutePatternRef?: RefObject<string | null>;
  /**
   * Ref to a provider that resolves + performs in-shell navigation for
   * `bridge.navigateToDeepLink`, built from `usePspContext()`'s app registry
   * and `useNavigate()`. Optional — callers that don't supply one (e.g.
   * unauthenticated/global app shells) simply don't get `navigateToDeepLink`
   * on their mounted module's bridge.
   */
  navigationProviderRef?: RefObject<NavigationProvider | null>;
  /**
   * Ref to a provider that switches the shell's active entity for
   * `bridge.setActiveEntity`, built from `useEntity()`'s
   * `setActiveEntityByFriendlyId`. Optional — callers that don't supply one don't
   * get `setActiveEntity` on their mounted module's bridge.
   */
  entityProviderRef?: RefObject<EntityProvider | null>;
  /**
   * When true, this mount claims the active routed remote for shell NavBar
   * aggregation (`ShellBridgeStore.setActiveRoutedAppId`). Use only from
   * `RemoteAppShell` (app registry + route registry). Global / unauthenticated
   * shells must leave this false so they never drive `remoteNav`.
   */
  claimActiveRemoteNav?: boolean;
}

/**
 * Return value from `useShellBridgeMount`.
 *
 * `mountError` is non-null when the most recent attempt to call `module.mount()`
 * threw. `retryMount` re-runs the mount effect against the same already-loaded
 * module — callers should use this rather than reloading the module on mount
 * failure (load failures are handled by `useModuleLoader`'s `reload`).
 */
export interface UseShellBridgeMountResult {
  mountError: Error | null;
  retryMount: () => void;
}

/**
 * Manages the mount/unmount lifecycle for a remote ES module, wiring a per-app
 * ShellBridge instance with both event subscriptions and a live Auth0 surface.
 *
 *   - Creates a ShellBridge before mount and passes it as shell: bridge in mount options
 *   - Builds an Auth0Provider that reads through `auth0Ref.current` so token
 *     refreshes and auth-state changes flow into mounted modules without
 *     triggering a remount
 *   - Disposes the bridge before unmount so UI contributions and subscriptions
 *     clear cleanly
 *
 * Auth values must never be added to `remountDeps` — the whole point of the
 * ref pattern is to avoid remounting on auth state change.
 *
 * Mount failure handling:
 *   - When `module.mount()` throws, the error is converted to an `Error`,
 *     stored in `mountError`, and logged structurally via the standard logger.
 *   - The bridge is disposed inside the catch (not deferred to cleanup) so
 *     header contributions and subscriptions clear immediately.
 *   - The container is cleared so partial DOM from a half-completed mount
 *     does not linger under the error UI.
 *   - Callers can call `retryMount()` to re-attempt mount against the same
 *     already-loaded module.
 *   - `module.unmount()` is still called best-effort in the effect cleanup
 *     (existing try/catch), even when mount previously threw. Skipping unmount
 *     entirely would leak React roots, listeners, and subscriptions for any
 *     module that completes partial setup before throwing.
 *
 * Health-check polling (PSS-2537):
 *   - After `module.mount()` returns successfully, the hook starts a 30s
 *     passive health-check poll via `startModuleHealthPoll` (see
 *     `services/moduleHealthPoll.ts`). The poll is started in a separate
 *     best-effort try/catch so an instrumentation failure cannot become a
 *     user-visible mount failure.
 *   - The poll is disposed in the existing cleanup, before `disposeAll()`
 *     and `module.unmount()`, so no further `healthCheck()` ticks fire
 *     against a module that's being torn down.
 */
export function useShellBridgeMount({
  module,
  containerRef,
  mountArgsRef,
  auth0Ref,
  remountDeps,
  appConfig,
  hostingEnvironment,
  showDebugInfoRef,
  resolvedModuleUrlRef,
  resolvedModuleRoutePatternRef,
  navigationProviderRef,
  entityProviderRef,
  claimActiveRemoteNav = false,
}: UseShellBridgeMountOptions): UseShellBridgeMountResult {
  const logger = useLoggerWithContext();
  const [mountError, setMountError] = useState<Error | null>(null);
  const [mountRetryCount, setMountRetryCount] = useState(0);

  const retryMount = useCallback(() => {
    setMountError(null);
    setMountRetryCount((count) => count + 1);
  }, []);

  useEffect(() => {
    // Clear any stale error from a previous attempt before this effect runs its
    // guards. Doing this unconditionally (even when the effect bails) means
    // stale `mountError` cannot survive a module/dep change.
    setMountError(null);

    const allDepsReady = remountDeps.every((dep) => dep != null);
    if (!module || !containerRef.current || !allDepsReady) return;

    const container = containerRef.current;
    const mountArgs = mountArgsRef.current;

    if (!mountArgs) {
      console.warn(`[RemoteAppShell] Mount args not yet available for ${appConfig.id}`);
      return;
    }

    // Build a provider that derefs auth0Ref.current on every call so the bridge
    // always reflects the latest auth state without the module remounting.
    const auth0Provider: Auth0Provider = {
      getAuth0State: (): Auth0State => {
        const ref = auth0Ref.current;
        if (!ref) {
          return {
            isAuthenticated: false,
            isLoading: false,
            user: undefined,
            error: undefined,
          };
        }
        return {
          isAuthenticated: ref.isAuthenticated,
          isLoading: ref.isLoading,
          user: ref.user ? { ...ref.user } : undefined,
          error: ref.error,
        };
      },
      // Auth0's getAccessTokenSilently is a stable useCallback inside the SDK,
      // so capturing the reference at mount time is safe. If we ever observe
      // reference instability we'd wrap and deref ref on each call instead.
      getAccessTokenSilently: ((options?: GetTokenSilentlyOptions) => {
        return auth0Ref.current!.getAccessTokenSilently(options as GetTokenSilentlyOptions);
      }) as Auth0Provider['getAccessTokenSilently'],
      getIdTokenClaims: () => auth0Ref.current!.getIdTokenClaims(),
      requestLogout: async () => {
        await auth0Ref.current!.logout();
      },
    };

    // Derefs navigationProviderRef.current on every call, same rationale as
    // auth0Provider above — avoids remounting the module if the caller's
    // app registry / navigate reference ever changes.
    const navigationProvider: NavigationProvider | undefined = navigationProviderRef
      ? {
          navigateToDeepLink: (deeplinkUrl, subject) => {
            navigationProviderRef.current?.navigateToDeepLink(deeplinkUrl, subject);
          },
        }
      : undefined;

    // Same ref-deref rationale as auth0Provider/navigationProvider — avoids
    // remounting the module when the EntityContext callback identity changes.
    const entityProvider: EntityProvider | undefined = entityProviderRef
      ? {
          setActiveEntity: (friendlyId) => {
            entityProviderRef.current?.setActiveEntity(friendlyId);
          },
        }
      : undefined;

    // disposeAll is `let` (not destructured const) so the mount-failure catch
    // block below can set it to undefined after eager-disposing — that prevents
    // the cleanup function from double-disposing.
    let disposeAll: (() => void) | undefined;
    const { bridge, disposeAll: createdDisposeAll } = createShellBridge(
      appConfig.id,
      shellBridgeStore,
      () => ({
        activeEntity: mountArgsRef.current?.activeEntity,
        hostingEnvironment,
        currentPath: window.location.pathname,
      }),
      shellEventBus,
      auth0Provider,
      navigationProvider,
      entityProvider,
      logger,
      // Mounted modules send events through the shell's own client, so the page
      // keeps one queue and one identity. Scoped to this mount and disposed with
      // the bridge; connected once `psp-og-analytics` resolves, even if the
      // module mounted first. Undefined when this build has no key, so modules
      // feature-detect with `shell.analytics?.track(...)`.
      createModuleAnalytics()
    );
    disposeAll = createdDisposeAll;
    const mountOptions: ModuleMountOptions = { ...mountArgs, shell: bridge };

    const startTime = performance.now();
    if (showDebugInfoRef.current) {
      console.log(`[RemoteAppShell] Mounting ${appConfig.id} at ${appConfig.basePath}`);
    }

    container.innerHTML = '';

    // PSS-2537: gated start of the passive health-check poll. We must set
    // `mountSucceeded = true` as the LAST statement inside the try block
    // (not immediately after `module.mount()`), so any throw later in the
    // try block — including from the debug logging or any future addition —
    // keeps the flag `false` and prevents the catch-then-start race.
    let disposeHealthPoll: (() => void) | undefined;
    let disposeRegistry: (() => void) | undefined;
    let mountSucceeded = false;
    let claimedActiveRemoteNav = false;

    try {
      // og_product is NOT set here. Mount happens after the pageview has
      // already been enqueued, so setting it here was always one step behind.
      // The SDK resolves it from the path on every location change instead.
      module.mount(container, mountOptions);

      if (showDebugInfoRef.current) {
        const mountTimeMs = performance.now() - startTime;
        console.log(`[RemoteAppShell] ${appConfig.id} mounted in ${mountTimeMs.toFixed(2)}ms`);
        console.log(`[RemoteAppShell] Module URL: ${resolvedModuleUrlRef.current}`);
      }

      mountSucceeded = true;
    } catch (err) {
      const errAsError = err instanceof Error ? err : new Error(String(err));

      console.error(`[RemoteAppShell] Mount failed for ${appConfig.id}:`, err);
      logger.error('Remote module mount failed', {
        appId: appConfig.id,
        basePath: appConfig.basePath,
        errorName: errAsError.name,
        error: errAsError.message,
        stack: errAsError.stack,
      });

      setMountError(errAsError);

      // Dispose the shell bridge eagerly so header contributions and subscriptions
      // clear while the user sits on the error UI. Set disposeAll to undefined so
      // the cleanup function below does not double-dispose.
      disposeAll?.();
      disposeAll = undefined;

      // Clear any partial DOM the throwing mount() left behind. The container
      // is already display:none under activeError, but a clean container avoids
      // ghost content if the user retries successfully.
      container.innerHTML = '';
    }

    // Run once: by this effect's cleanup, or earlier by the logout fan-out, which tears mounted
    // modules down before the Auth0 redirect. The page stays when that redirect fails, so the
    // cleanup still runs afterwards. Throws what `module.unmount()` throws.
    let tornDown = false;
    const teardown = () => {
      if (tornDown) return;
      tornDown = true;

      if (showDebugInfoRef.current) {
        console.log(`[RemoteAppShell] Unmounting ${appConfig.id}`);
      }

      // Unregister from the logout fan-out registry first — the module
      // is leaving the mount lifecycle, so it should no longer receive
      // `onSessionEnd`. The dispose is idempotent and a no-op when mount threw.
      disposeRegistry?.();

      // Drop active-nav claim before disposing contributions so the shell
      // NavBar does not briefly show this app's config after unmount starts.
      // No-op if another RemoteAppShell already claimed a newer active id.
      if (claimedActiveRemoteNav) {
        shellBridgeStore.clearActiveRoutedAppId(appConfig.id);
      }

      // PSS-2537: stop the health poll before tearing down the bridge or
      // unmounting the module. Idempotent: noop dispose when `healthCheck`
      // was absent, undefined when mount threw, and the helper's own
      // `disposed` flag guards against any in-flight tick.
      disposeHealthPoll?.();

      // Dispose bridge BEFORE unmount so UI contributions clear even if unmount throws.
      // No-op when the catch block above already disposed.
      disposeAll?.();

      // Best-effort unmount, even when the previous mount() threw — a partial mount
      // may have created React roots, listeners, or subscriptions that the module's
      // own unmount() is best positioned to clean up.
      try {
        module.unmount();
      } finally {
        // Force clear container after unmount to prevent orphaned React roots
        if (container?.parentElement) {
          container.innerHTML = '';
        }
      }
    };

    // PSS-2537: start the passive 30s health-check poll only after a
    // successful mount, in a separate best-effort try/catch. Instrumentation
    // startup must NEVER become a user-visible mount failure, so even if
    // `startModuleHealthPoll()` itself throws (or the warn-log call below
    // throws), we keep the mounted module visible.
    if (mountSucceeded) {
      // Register so logout fan-out can iterate this module's onSessionEnd
      // hook. Only after successful mount — a half-mounted module is not safe to
      // notify of session end.
      disposeRegistry = registerMountedModule({ appId: appConfig.id, module, teardown });

      // Not undone on unmount: the module's session outlives its mount, so sign-out has to
      // reach it after the user leaves the app, reloads, or signs out from another tab. Guarded
      // like the module's other calls: the opt-in is read off the remote module's object.
      try {
        if (endsSessionWhenUnmounted(module)) {
          const routePattern = resolvedModuleRoutePatternRef?.current;
          const sessionEndModule = routePattern ? { appId: appConfig.id, routePattern } : undefined;
          const mountedUrl = resolvedModuleUrlRef.current;
          if (
            !sessionEndModule ||
            !mountedUrl ||
            sessionEndModuleUrl(sessionEndModule) !== mountedUrl
          ) {
            // Sign-out loads a module by its app id and route from the registries as the code
            // defines them. This mount isn't one of theirs (a global or unauthenticated shell, or
            // a dev-tool override URL), so sign-out would reach some other module, or none.
            logger.warn(
              'Module opts in to session end when unmounted, but sign-out cannot load it',
              { appId: appConfig.id }
            );
          } else {
            rememberSessionEnd(sessionEndModule, mountedUrl);
          }
        }
      } catch (err) {
        logger.warn('Could not remember module for session end', {
          appId: appConfig.id,
          error: err instanceof Error ? err.message : String(err),
        });
      }

      if (claimActiveRemoteNav) {
        shellBridgeStore.setActiveRoutedAppId(appConfig.id);
        claimedActiveRemoteNav = true;
      }

      try {
        disposeHealthPoll = startModuleHealthPoll({
          module,
          appConfig,
          logger,
        });
      } catch (err) {
        const errAsError = err instanceof Error ? err : new Error(String(err));
        try {
          logger.warn('Module health poll failed to start', {
            appId: appConfig.id,
            basePath: appConfig.basePath,
            errorName: errAsError.name,
            error: errAsError.message,
            stack: errAsError.stack,
          });
        } catch {
          // Health telemetry is best-effort; keep the mounted module visible.
        }
      }
    }

    return () => {
      // The try/catch keeps a throwing unmount() from crashing the hook.
      try {
        teardown();
      } catch (err) {
        console.error(`[RemoteAppShell] Unmount failed for ${appConfig.id}:`, err);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, mountRetryCount, ...remountDeps]);

  return { mountError, retryMount };
}
